// ============================================================
// Stocora — AuthCallbackPage (rewritten 2026-07-19: password auth)
// ============================================================
// With magic-link login removed, this route is now reached ONLY
// by two link types, both of which require the user to set a
// password next:
//   1. Invite links (invite-clinic-member → inviteUserByEmail)
//   2. Password-reset links (LoginPage "Forgot password?")
//
// Both use PKCE; with `detectSessionInUrl: true` the supabase-js
// client auto-exchanges the `?code=...` URL param for a session on
// load. This page's job is unchanged in mechanism — wait for that
// exchange, then redirect — but the destination changed: instead
// of going straight to `?next=` (or /scan), it ALWAYS hands off to
// /set-password (carrying `?next=` through). There's no longer a
// reason to distinguish invite-vs-recovery here: either way the
// very next required step is "set a password," so one downstream
// page (SetPasswordPage) handles both.
//
// `?next=` threading: LoginPage / the invite email's redirectTo
// append the desired post-login destination; we read it here and
// pass it straight through to SetPasswordPage, which does the
// final navigate after the password is saved.
//
// Three failure surfaces we handle:
//   1. Supabase returned an error — via URL params for some cases,
//      but via the HASH fragment (#error=...&error_code=otp_expired
//      &error_description=...) for expired/already-consumed links.
//      Confirmed 2026-07-20: a real invite hit this exact path — the
//      error never surfaced because searchParams only reads the query
//      string, so it silently fell through to the session check
//      below, found a stale pre-existing session from earlier owner
//      testing in the same browser, and wrongly treated THAT as proof
//      this link worked. Landed the user on SetPasswordPage with the
//      wrong session, which then 403'd on save. Root cause of the
//      otp_expired itself is very likely email link prefetching
//      (Microsoft Defender Safe Links on the hotmail.co.uk recipient
//      consumed the single-use code before the human clicked) — same
//      failure class as the already-documented approval-link
//      scanner-prefetch issue, hitting a new surface. The auto-fire-
//      on-load fix (requiring an explicit tap before exchange) is a
//      design fork, not made here — this fix only closes the code bug
//      (hash errors were invisible) so a genuinely-expired link always
//      bounces to /login with a real message instead of silently
//      finding an unrelated stale session.
//   2. The auth state listener never fires SIGNED_IN (timeout)
//   3. getSession() returns an error
// All failures redirect to /login with ?error= so LoginPage can
// render a message.
// ============================================================

import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { DEFAULT_POST_LOGIN_PATH, safeNextPath } from '@/lib/auth';
import Icon from '@/components/Icon';

// If the code exchange hasn't completed in this window, something
// is wrong — bounce back to /login rather than hanging forever.
const EXCHANGE_TIMEOUT_MS = 15_000;

/**
 * Supabase reports link errors (expired/invalid/already-consumed —
 * e.g. otp_expired) via the URL HASH fragment, not the query string.
 * useSearchParams() never sees these — location.hash has to be
 * parsed directly. Returns null when no error is present.
 */
function readHashError(): { error: string; description: string | null } | null {
  const hash = window.location.hash.startsWith('#')
    ? window.location.hash.slice(1)
    : window.location.hash;
  if (!hash) return null;
  const params = new URLSearchParams(hash);
  const error = params.get('error');
  if (!error) return null;
  return { error, description: params.get('error_description') };
}

export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Validated post-login destination, threaded through to
    // SetPasswordPage as its own `?next=` rather than used directly
    // here — see header comment.
    const destination = safeNextPath(searchParams.get('next'));
    const setPasswordUrl = new URL('/set-password', window.location.origin);
    if (destination !== DEFAULT_POST_LOGIN_PATH) {
      setPasswordUrl.searchParams.set('next', destination);
    }
    const setPasswordPath = `${setPasswordUrl.pathname}${setPasswordUrl.search}`;

    // 1a. Hash-based error check FIRST — must run before any session
    //     check below, or a stale pre-existing session in this
    //     browser can mask a genuinely failed/expired link (see
    //     header comment for the 2026-07-20 incident this fixes).
    const hashError = readHashError();
    if (hashError) {
      const query = new URLSearchParams({ error: hashError.error });
      if (hashError.description) {
        query.set('error_description', hashError.description);
      }
      navigate(`/login?${query.toString()}`, { replace: true });
      return;
    }

    // 1b. Query-string error check — some Supabase error paths use
    //     ?error=... instead of the hash. Kept for that case.
    const errorParam = searchParams.get('error');
    if (errorParam) {
      const description = searchParams.get('error_description');
      const query = new URLSearchParams({ error: errorParam });
      if (description) {
        query.set('error_description', description);
      }
      navigate(`/login?${query.toString()}`, { replace: true });
      return;
    }

    let handled = false;

    const finish = (path: string): void => {
      if (handled) return;
      handled = true;
      navigate(path, { replace: true });
    };

    // 2. Happy path: the client may have already exchanged the code
    //    before this effect ran (detectSessionInUrl is synchronous
    //    on module load in many cases). Check first.
    void supabase.auth.getSession().then(({ data, error }) => {
      if (handled) return;
      if (error) {
        finish(
          `/login?error=session_error&error_description=${encodeURIComponent(error.message)}`,
        );
        return;
      }
      if (data.session) {
        finish(setPasswordPath);
      }
      // else: wait for SIGNED_IN via the listener below.
    });

    // 3. Listener for the async case — exchange completes after mount.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session) {
        finish(setPasswordPath);
      }
    });

    // 4. Timeout fallback — something's wrong if we haven't been
    //    signed in within the window.
    const timeoutId = window.setTimeout(() => {
      finish('/login?error=exchange_timeout');
    }, EXCHANGE_TIMEOUT_MS);

    return () => {
      subscription.unsubscribe();
      window.clearTimeout(timeoutId);
    };
  }, [navigate, searchParams]);

  return (
    <div className="p-6 flex flex-col items-center justify-center min-h-full text-center">
      <div className="w-12 h-12 rounded-2xl bg-action-weak flex items-center justify-center mb-4">
        <Icon name="mail" className="w-6 h-6 text-action" strokeWidth={2} />
      </div>
      <p className="text-sm font-medium text-slate-700">Signing you in…</p>
      <p className="text-xs text-slate-500 mt-1">One moment.</p>
    </div>
  );
}
